import classNames from 'classnames';
import { throttle } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'utils/i18n';

import QuickSearch from '../../containers/quickSearch';
import useIsMobile from '../../hooks/useIsMobile';
import { AuthContext } from '../../state/auth';
import { BasketContext } from '../../state/basket';

interface Props {
  pageId: string;
}

interface SignedInNavProps {
  basketItemsCount: number;
  handleLogout: () => void;
}

const HeaderNav: FC<Props> = ({ pageId }) => {
  const { t } = useTranslation('common');
  const { actions, loggedIn } = useContext(AuthContext);
  const { items } = useContext(BasketContext);

  const router = useRouter();
  const isMobileView = useIsMobile();
  const hideRightNavLinks = router.pathname === '/bi';

  const [stickyNavVisible, toggleStickyNavVisibility] = useState(false);
  const [quickSearchVisible, toggleQuickSearchVisibility] = useState(false);

  const classes = classNames({
    header: true,
    'header--absolute': !stickyNavVisible,
    'header--sticky': stickyNavVisible,
  });

  useEffect(() => {
    const pageUrls = [
      'home',
      'book-consulting-rooms',
      'book-minor-operations-rooms',
      'book-treatment-rooms',
      'book-examination-rooms',
      'book-counselling-rooms',
      'book-group-rooms',
      'book-meeting-rooms',
      'book-office-rooms',
    ];

    const verticalBreakpoint = 515;

    const handleScroll = () => {
      if (!isMobileView) {
        if (window.pageYOffset > verticalBreakpoint) {
          toggleStickyNavVisibility(true);

          if (pageUrls.includes(pageId)) {
            toggleQuickSearchVisibility(true);
          }
        } else {
          toggleStickyNavVisibility(false);

          if (pageUrls.includes(pageId)) {
            toggleQuickSearchVisibility(false);
          }
        }
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 500);

    if (pageUrls.includes(pageId)) {
      window.addEventListener('scroll', throttledHandleScroll);
    } else {
      toggleStickyNavVisibility(true);
      toggleQuickSearchVisibility(true);
    }

    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [isMobileView, pageId]);

  const SignedInNav: FC<SignedInNavProps> = ({
    basketItemsCount,
    handleLogout,
  }) => (
    <>
      <li className="header__nav-item">
        <div className="dropdown">
          {t('account.title')}
          <img src="/images/icons/homepage/navigation/chevron.svg" alt="" />
          <div className="dropdown__content">
            <ul>
              <li>
                <Link href={t('navigation.bookings.url')}>
                  {t('navigation.bookings.text')}
                </Link>
              </li>
              <li>
                <Link href={t('navigation.accounts.url')}>
                  {t('navigation.accounts.text')}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  data-testid="logoutBtn"
                  onClick={handleLogout}
                >
                  {t('navigation.sign_out.text')}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </li>
      <li className="header__nav-item basket-link">
        <Link href={t('navigation.basket.url')}>
          {t('navigation.basket.text')}
        </Link>
        <span className="basket-link__count">{basketItemsCount}</span>
      </li>
    </>
  );

  const SignedOutNav: FC = () => (
    <>
      <li className="header__nav-item">
        <Link
          href={t('navigation.sign_in.url')}
          onClick={() => {
            if (router.pathname !== t('navigation.sign_in.url'))
              (actions.setReturnUrl as (url: string) => void)(router.asPath);
          }}
          className="button button--outline button--outline-secondary button--secondary-text"
          data-cy="signInButton"
        >
          {t('navigation.sign_in.text')}
        </Link>
      </li>
      <li className="header__nav-item">
        <Link
          href={t('navigation.register.url')}
          className="button button--primary button--white-text"
        >
          {t('navigation.register.text')}
        </Link>
      </li>
    </>
  );

  return (
    <div className={classes}>
      <div className="header__nav" data-cy="navbar">
        <Link href="/">
          <div className="header__logo header__logo--absolute">
            <img src="/images/openspace-logo.svg" alt="NHS Open Space" />
          </div>
          <div className="header__logo header__logo--sticky">
            <img src="/images/openspace-logo.svg" alt="NHS Open Space" />
          </div>
        </Link>

        {quickSearchVisible && <QuickSearch />}

        <ul className="header__nav-list">
          <li className="header__nav-item find-rooms">
            <Link href={t('navigation.search.url')}>
              {t('navigation.search.text')}
            </Link>
          </li>
          <li className="header__nav-item find-rooms">
            <Link href={t('navigation.properties.url')}>
              {t('navigation.properties.text')}
            </Link>
          </li>
        </ul>

        {!hideRightNavLinks && (
          <ul className="header__nav-list header__nav-list--pull">
            <li className="header__nav-item properties">
              <Link href={t('navigation.properties.url')}>
                {t('navigation.properties.text')}
              </Link>
            </li>
            <li className="header__nav-item">
              <Link href={t('navigation.help.url')}>
                {t('navigation.help.text')}
              </Link>
            </li>
            {loggedIn ? (
              <SignedInNav
                basketItemsCount={items?.length ?? 0}
                handleLogout={() => actions.signOut()}
              />
            ) : (
              <SignedOutNav />
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default HeaderNav;
